import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import { useTranslation } from "react-i18next";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
  IconButton,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useEmail from "../../../hooks/useUserActivity";
import AdvanceFilter from "./AdvanceFilter";
import useUserActivity from "../../../hooks/useUserActivity";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import Visibility from "@mui/icons-material/Visibility";
import ViewUpdateDetails from "./ViewUpdateDetails";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function UserActivity(props) {
  const { isLoadingDownload, downloadFileType } = useUserActivity();
  const { width: mainContentWidth } = useMainContentDimensions();

  const { state } = useLocation();
  const { t } = useTranslation();

  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
    },
    sortFields: [],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const { height } = useWindowDimensions();
  const { getAllUserActivity, userActivities, paging, downloadFile } = useEmail();
  const { openDialog } = useDialog();

  const [payload, setPayload] = useState(currentPayload);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    await getAllUserActivity(payload);
  };

  const refreshPage = () => {
    setPayload(currentPayload);
    filterResetFunc();
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>logs</Typography>
        <Typography>user-activities</Typography>
        <Typography>index</Typography>
      </Breadcrumbs>
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "moduleName",
      headerName: "Module Name",
      width: 300,
    },
    {
      field: "actionType",
      headerName: "Action Type",
      width: 150,
    },
    {
      field: "action",
      headerName: "Changed Fields",
      width: 200,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      valueGetter: (params) => params?.name,
      type: "string",
      width: 300,
    },
    {
      field: "actionDate",
      headerName: "Created On",
      width: 200,
    },
    {
      field: "view",
      headerName: "View",
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => openViewDialog(params.row)} color="primary" size="small">
          <Visibility />
        </IconButton>
      ),
    },
  ];

  const openViewDialog = (row) => {
    return openDialog(
      "view_details",
      "Update Details",
      <ViewUpdateDetails oldValue={row.oldValue} newValue={row.newValue} />,
    );
  };

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
      />,
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            downloadFile={downloadFile}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {t("user_activities")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            columns={columns}
            refreshPage={fetchData}
            rows={userActivities}
            getRowId={(row) => row["userActivityId"]}
            rowCount={paging?.totalNumberOfRecords || 0}
            loading={false}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default UserActivity;
